
import { TransactionHeader } from '../transaction-config';
import { useTransactionEnricher } from './useTransactionEnricher';
import {
    ActionStatusHelper,
    DomainTableConfig,
    useAppEnum,
    ActionResponse,
    useLayoutLifeCycle,
} from '@/Shared';
import { DOMAIN_CONFIGS, DomainHookProps } from '@/Shared/Config/domain-config';
import { useTransaction } from '@/OneLiners';
import { TransactionViewModel } from '../ViewModels/TransactionViewModel';
import { TransactionModel } from '../TransactionModel';


/**
 * useTransactionTableConfig hook
 *
 * Domain-specific layout composition hook for Transaction entities.
 * Orchestrates domain data, enrichment, permissions, and UI table configuration.
 *
 * Responsibilities:
 * - Aggregate Transaction domain state, enrichment, and permissions into a single response.
 * - Provide dynamic headers for Transaction table.
 * - Map strict Model → ViewModel for UI consumption.
 * - Delegate lifecycle orchestration to useLayoutLifeCycle.
 *
 * @date: March 2, 2026
 * @author: Samano CASTRE
 *
 * @see DomainHookProps
 * @see DomainTableConfig<TransactionViewModel>
 *
 * @param props Domain-level configuration and permissions
 * @returns DomainTableConfig<TransactionViewModel>
 */
export function useTransactionTableConfig(props: DomainHookProps): DomainTableConfig<TransactionViewModel> {
    const { permissionHook, accessRight } = props;
    const { domainType } = DOMAIN_CONFIGS.Transaction;

    const { TransactionEnums } = useAppEnum();

    const transactionHook = useTransaction();

    /**
     * Enrichment layer
     * Responsible for resolving user- and contact-related data,
     * and computing derived Transaction fields.
     */
    const {
        userMap,
        contactMap,
        getStrictEnrichedTransaction,
        enricherResponse,
    } = useTransactionEnricher();

    /**
     * Combined readiness response
     * Only builds table once all sources succeeded
     */
    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            transactionHook.stateResponse.status,
            enricherResponse.status,
            permissionHook.stateResponse.status
        ),
        error: transactionHook.stateResponse.error ??
            enricherResponse.error
    };

    /**
     * Delegation to generic layout lifecycle.
     */
    const lifeCycleHook = useLayoutLifeCycle({
        response,
        domainType,
        props,
        entityHook: transactionHook,

        /**
         * Header configuration for Property table.
         */
        headers: TransactionHeader.build(
            contactMap,
            userMap,
            TransactionEnums
        ),

        /**
         * Strict Model → ViewModel transformation.
         */
        toViewModelFn: (transaction: TransactionModel, isDisplaying: boolean) =>
            new TransactionViewModel({
                transaction: getStrictEnrichedTransaction(transaction),
                viewEnums: TransactionEnums,
                accessRight,
                isDisplaying
            })
    });

    return lifeCycleHook;
}